import { reactive } from 'cdui-js';
import { goBackByBrowser } from '../../../../../lib/browser';
import { aesDecryptHexByPass } from '../../../../../lib/crypto';
import { postToApp } from '../../../../../lib/rnBridge';
import { commonState, waitForEncryptKey } from '../../../../../stores/common';
import { addAccState } from '../store';
import { readNameParts } from '../../nameParts';

/** 境外身份类型（不含大陆） */
export type OverseasIdentityType = 'HONG_KONG' | 'MACAO' | 'OTHER_REGION';

const OVERSEAS_ID_TYPES = new Set<string>(['HONG_KONG', 'MACAO', 'OTHER_REGION']);

const emptyUploadLicForm = () => ({
  firstNameEn: '',
  lastNameEn: '',
  fullName: '',
  pinyinName: '',
  gender: '',
  idCardNo: '',
  idCardStartDate: '',
  idCardEndDate: '',
  idCardLongTerm: 0 as 0 | 1,
  birthCountry: '',
  birthDate: '',
  idCardIssueAuthority: '',
});

export const virtualApplyState = reactive({
  step: 1,
  /** 加开申请 busId（saveApply 返回） */
  busId: '',
  overseasIdentityType: '' as OverseasIdentityType | '',
  identityFrontUrl: '',
  identityBackUrl: '',
  /** 境外证件表单（对齐开户 UploadLic） */
  uploadLicForm: emptyUploadLicForm(),
  addressForm: {
    country: '',
    address: '',
    phone: '',
    email: '',
  },
  addressProveUrls: [] as string[],
});

export const resetVirtualApplyState = () => {
  virtualApplyState.step = 1;
  virtualApplyState.busId = '';
  virtualApplyState.overseasIdentityType = '';
  virtualApplyState.identityFrontUrl = '';
  virtualApplyState.identityBackUrl = '';
  virtualApplyState.uploadLicForm = emptyUploadLicForm();
  virtualApplyState.addressForm = {
    country: '',
    address: '',
    phone: '',
    email: '',
  };
  virtualApplyState.addressProveUrls = [];
};

const safeDecrypt = (encryptedHex?: string) => {
  if (!encryptedHex || !commonState.encryptKey) return '';
  try {
    return aesDecryptHexByPass(encryptedHex, commonState.encryptKey);
  } catch {
    return '';
  }
};

const scheduleIdCardDecryptRetry = (idCardNoEncrypt?: string) => {
  if (!idCardNoEncrypt || commonState.encryptKey) return;
  void waitForEncryptKey().then((key) => {
    if (!key || virtualApplyState.uploadLicForm.idCardNo) return;
    virtualApplyState.uploadLicForm.idCardNo = safeDecrypt(idCardNoEncrypt);
  });
};

const findAttachUrl = (list: any[] | undefined, type: string) => {
  if (!Array.isArray(list)) return '';
  const hit = list.find((i) => i?.attachType === type);
  return hit?.fileUrl || '';
};

const findAttachUrls = (list: any[] | undefined, type: string) => {
  if (!Array.isArray(list)) return [] as string[];
  return list.filter((i) => i?.attachType === type && i?.fileUrl).map((i) => String(i.fileUrl));
};

/** 从 getByUserId(ADD_ACCOUNT) 回填境外身份补充页（BACK 续办） */
export const hydrateVirtualApplyFromApplyData = async (data: any) => {
  await waitForEncryptKey();

  const base = data?.userBase || {};
  const busId = String(data?.id || addAccState.busId || '');
  const attachments = data?.attachmentList || [];
  const idCardType = String(base.idCardType || '');
  const overseasType = OVERSEAS_ID_TYPES.has(idCardType) ? (idCardType as OverseasIdentityType) : '';

  virtualApplyState.busId = busId;
  virtualApplyState.overseasIdentityType = overseasType;
  virtualApplyState.identityFrontUrl = findAttachUrl(attachments, 'identity_front');
  virtualApplyState.identityBackUrl = findAttachUrl(attachments, 'identity_back');
  virtualApplyState.addressProveUrls = findAttachUrls(attachments, 'address_prove');

  const idCardNo = base.idCardNoEncrypt ? safeDecrypt(base.idCardNoEncrypt) : '';
  const names = readNameParts(base);
  virtualApplyState.uploadLicForm = {
    firstNameEn: names.firstNameEn,
    lastNameEn: names.lastNameEn,
    fullName: names.fullName,
    pinyinName: names.pinyinName,
    gender: base.gender || '',
    idCardNo,
    idCardStartDate: base.idCardStartDate || '',
    idCardEndDate: base.idCardEndDate || '',
    idCardLongTerm: (base.idCardLongTerm ?? 0) as 0 | 1,
    birthCountry: base.birthCountry || '',
    birthDate: base.birthDate || '',
    idCardIssueAuthority: base.idCardIssueAuthority || '',
  };

  if (base.idCardNoEncrypt && !idCardNo) {
    scheduleIdCardDecryptRetry(base.idCardNoEncrypt);
  }

  virtualApplyState.addressForm = {
    country: base.country || '',
    address: base.address || '',
    phone: base.phone || '',
    email: base.email || '',
  };

  // 大陆用户必须先看到港澳及其他地区证件页。开户居住地址 / 地址证明不能跳过。
  // 已有境外证件类型或证件照 → 上传页；否则从选择证件开始。
  if (overseasType || virtualApplyState.identityFrontUrl) {
    virtualApplyState.step = 2;
  } else {
    virtualApplyState.step = 1;
  }

  addAccState.busId = busId || addAccState.busId;
  addAccState.virtualResume = true;
};

export const lastVirtualStep = () => {
  if (virtualApplyState.step > 1) {
    virtualApplyState.step--;
  } else if (addAccState.virtualFlow) {
    // 同页嵌入流程：回到加开账户类型选择
    addAccState.virtualFlow = false;
  } else {
    goBackByBrowser();
  }
};

export const nextVirtualStep = () => {
  virtualApplyState.step++;
};

/** 境外身份补充完成 / 非大陆用户：通知 App 继续加开虚拟账户（带上当前语言/主题） */
export const notifyAppContinueAddVirtual = (payload?: Record<string, unknown>) => {
  postToApp({
    type: 'WEB_TO_APP_ContinueAddVirtualAccount',
    payload: {
      securitiesAccountType: 'CRYPTO_ACCOUNT',
      busId: (payload?.busId as string) || virtualApplyState.busId || undefined,
      overseasIdentityType: virtualApplyState.overseasIdentityType || undefined,
      lang: commonState.langauge,
      theme: commonState.theme,
      scheme: commonState.theme,
      ...payload,
    },
  });
};

/** 通知 App 打开专业投资者认证页（与 ContinueAddVirtualAccount 分开） */
export const notifyAppOpenProfessionalInvestor = () => {
  postToApp({
    type: 'WEB_TO_APP_OpenProfessionalInvestor',
    timestamp: Date.now(),
  });
};
